<!--
// app/components/pages/mercato/TransfersSection.vue
-->
<script setup lang="ts">
import { setAliasUrl } from "~~/src/Shared/utils";
import type { MercatoTeam } from "~~/src/mercato/domain/models/MercatoTeam";
import type { MercatoTransfer } from "~~/src/mercato/domain/models/MercatoTransfer";
import ConceptLink from "~/components/glossary/ConceptLink.vue";

type Props = {
  teams: MercatoTeam[];
  league: string;
};

type TransferGroup = {
  tradeId: string;
  mainTransfer: MercatoTransfer | null; // Make nullable
  relatedTransfers: MercatoTransfer[];
  date: string;
  window: string;
  status: string;
  scope: string;
  transferType: string;
};

const props = defineProps<Props>();
const { $t, $tn, $getLocale } = useI18n();
const locale = $getLocale();

// Fetch transfers data
const { data: transfers, error: transfersError } = await useLazyAsyncData(
  "mercatoTransfers",
  (_nuxtApp, { signal }) => $fetch<MercatoTransfer[]>("/api/v1/mercato/transfers", { signal })
);
if (transfersError.value) console.error(transfersError.value);

// Create team lookup object for fast access
const theTeams = props.teams.reduce(
  (acc, cur) => {
    acc[String(cur.id)] = cur;
    return acc;
  },
  {} as Record<string, MercatoTeam>
);

// Filter state: active or closed
const statusFilter = ref<"active" | "closed">("closed");

// Helper functions
const getTeamName = (teamId: string): string => {
  return theTeams[teamId]?.completeName || "Unknown Team";
};

const getTeamShortName = (teamId: string): string => {
  return theTeams[teamId]?.shortName || "UT";
};

const getTeamLogo = (teamId: string): string | null => {
  return theTeams[teamId]?.logo?.url || null;
};

const getTeamLeague = (teamId: string): string | undefined => {
  if (teamId === "0") return undefined;
  return theTeams[teamId]?.league;
};

const isTransferActive = (status: string): boolean => {
  return status === "ROFR" || status === "Player Decision" || status === "Negotiation";
};

const getTransferTypeConceptId = (transferType: string): string | null => {
  const typeMap: Record<string, string> = {
    Trade: "concept-trade",
    Transfer: "concept-transfer",
    "Release Clause": "concept-release-clause",
  };
  return typeMap[transferType] || null;
};

// Computed: Filtered and sorted transfers
const filteredTransfers = computed(() => {
  if (!transfers.value || !Array.isArray(transfers.value)) return [];

  const filtered = transfers.value.filter(transfer => {
    // League filter: check both oldTeamId and newTeamId
    if (props.league && props.league !== "all") {
      const oldTeamLeague = getTeamLeague(transfer.oldTeamId);
      const newTeamLeague = getTeamLeague(transfer.newTeamId);
      const matchesLeague = oldTeamLeague === props.league || newTeamLeague === props.league;
      if (!matchesLeague) return false;
    }

    // Status filter: active or closed
    const status = transfer.status?.trim() || "";

    if (statusFilter.value === "active") {
      // Active: only ROFR or Player Decision
      return status && isTransferActive(status);
    } else {
      // Closed: status is "closed" (case insensitive) or empty/no status
      return !status || status.toLowerCase() === "closed";
    }
  });

  // Sort by date, most recent first
  return filtered.sort((a, b) => {
    if (!a.date || !b.date) return 0;
    return b.date.localeCompare(a.date);
  });
});

const groupedTransfers = computed(() => {
  if (filteredTransfers.value.length === 0) return [];

  const groups: Map<string, TransferGroup> = new Map();

  filteredTransfers.value.forEach(transfer => {
    const tradeId = transfer.tradeId?.trim() || `single-${transfer.playerId}-${transfer.date}`;

    if (!groups.has(tradeId)) {
      const isMainTransfer = transfer.isMain === "TRUE";
      groups.set(tradeId, {
        tradeId,
        mainTransfer: isMainTransfer ? transfer : null, // Remove the !
        relatedTransfers: isMainTransfer ? [] : [transfer],
        date: transfer.date,
        window: transfer.window,
        status: transfer.status,
        scope: transfer.scope,
        transferType: transfer.transferType,
      });
    } else {
      const group = groups.get(tradeId)!;
      if (transfer.isMain === "TRUE") {
        if (group.mainTransfer) {
          // Keep the most recent one
          const existingDate = new Date(group.mainTransfer.date).getTime();
          const newDate = new Date(transfer.date).getTime();

          if (newDate > existingDate) {
            // Demote existing main to related
            group.relatedTransfers.push(group.mainTransfer);
            // Promote new transfer to main
            group.mainTransfer = transfer;
            group.date = transfer.date;
            group.window = transfer.window;
            group.status = transfer.status;
            group.scope = transfer.scope;
            group.transferType = transfer.transferType;
          } else {
            // Add newer transfer to related
            group.relatedTransfers.push(transfer);
          }
          console.warn(`Multiple isMain transfers for tradeId: ${tradeId}. Keeping most recent.`);
        } else {
          group.mainTransfer = transfer;
          group.date = transfer.date;
          group.window = transfer.window;
          group.status = transfer.status;
          group.scope = transfer.scope;
          group.transferType = transfer.transferType;
        }
      } else {
        group.relatedTransfers.push(transfer);
      }
    }
  });

  const groupsArray = Array.from(groups.values()).map(group => {
    if (!group.mainTransfer && group.relatedTransfers.length > 0) {
      // Create a new object instead of mutating
      return {
        ...group,
        mainTransfer: group.relatedTransfers[0],
        relatedTransfers: group.relatedTransfers.slice(1),
      };
    }
    return group;
  });

  // Sort by group date (from mainTransfer)
  return groupsArray.sort((a, b) => {
    if (!a.date || !b.date) return 0;
    return b.date.localeCompare(a.date);
  });
});
</script>

<template>
  <section ref="transfersSection" id="transfers" class="kql-w-container px-4 xl:px-0 my-10">
    <!-- Status Filter Buttons -->
    <div class="flex flex-wrap items-center justify-center mb-8">
      <button
        type="button"
        :class="['status-filter-btn rounded-s-lg', { active: statusFilter === 'active' }]"
        :aria-pressed="statusFilter === 'active'"
        :aria-label="$t('mercato.transfers.active')"
        @click="statusFilter = 'active'"
      >
        {{ $t("mercato.transfers.active") }}
      </button>
      <button
        type="button"
        :class="['status-filter-btn rounded-e-lg', { active: statusFilter === 'closed' }]"
        :aria-pressed="statusFilter === 'closed'"
        :aria-label="$t('mercato.transfers.closed')"
        @click="statusFilter = 'closed'"
      >
        {{ $t("mercato.transfers.closed") }}
      </button>
    </div>

    <!-- Transfers List -->
    <div v-if="groupedTransfers && groupedTransfers.length > 0" class="transfers-list">
      <div v-for="(group, groupIndex) in groupedTransfers" :key="group.tradeId" class="transfer-card-group">
        <div class="transfer-card">
          <!-- Left Info -->
          <div class="transfer-card__left-info">
            <span class="transfer-card__window">
              {{ group.window }}
            </span>

            <!-- Status badge (mobile only) -->
            <div
              v-if="group.status && isTransferActive(group.status)"
              class="transfer-card__status-mobile md:hidden"
            >
              <concept-link
                :concept="
                  group.status === 'ROFR'
                    ? 'concept-rofr'
                    : group.status === 'Negotiation'
                      ? 'concept-negotiation'
                      : 'concept-player-decision'
                "
              >
                {{ group.status }}
              </concept-link>
            </div>

            <!-- Scope with Icon -->
            <div
              v-if="group.scope"
              :class="['transfer-card__scope', `transfer-card__scope--${group.scope.toLowerCase()}`]"
            >
              <!-- Global Icon -->
              <svg
                v-if="group.scope.toLowerCase() === 'global'"
                class="transfer-card__scope-icon"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                ></path>
              </svg>
              <!-- Local Icon -->
              <svg
                v-else
                class="transfer-card__scope-icon"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                ></path>
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                ></path>
              </svg>
              <span>{{ $t(`mercato.transfers.${group.scope}`) }}</span>
            </div>
          </div>

          <!-- Center: Transfer Flow -->
          <div class="transfer-card__flow">
            <!-- Main Transfer Flow -->
            <div class="transfer-card__main-flow">
              <!-- Old Team -->
              <div class="transfer-card__team-wrapper">
                <div class="transfer-card__team">
                  <NuxtImg
                    v-if="getTeamLogo(group.mainTransfer.oldTeamId)"
                    :src="setAliasUrl(getTeamLogo(group.mainTransfer.oldTeamId)!)"
                    :alt="getTeamName(group.mainTransfer.oldTeamId)"
                    width="48"
                    height="48"
                    sizes="48px"
                    class="transfer-card__logo"
                    loading="lazy"
                  />
                  <div v-else class="transfer-card__logo-placeholder"></div>
                  <p class="transfer-card__team-name hidden sm:block">
                    {{ getTeamName(group.mainTransfer.oldTeamId) }}
                  </p>
                  <p class="transfer-card__team-name sm:hidden">
                    {{ getTeamShortName(group.mainTransfer.oldTeamId) }}
                  </p>
                </div>
              </div>

              <!-- Player with Arrow -->
              <div class="transfer-card__player-section">
                <div class="transfer-card__arrow-line">
                  <div
                    :class="[
                      'transfer-card__arrow-left',
                      { dashed: group.status && isTransferActive(group.status) },
                    ]"
                  ></div>
                  <div class="transfer-card__player-badge">
                    <p class="transfer-card__player-name">{{ group.mainTransfer.playerName }}</p>
                  </div>
                  <div
                    :class="[
                      'transfer-card__arrow-right',
                      { dashed: group.status && isTransferActive(group.status) },
                    ]"
                  >
                    <!-- Status on arrow for active transfers (desktop only) -->
                    <div
                      v-if="group.status && isTransferActive(group.status)"
                      class="transfer-card__status-on-arrow hidden md:block"
                    >
                      <concept-link
                        :concept="
                          group.status === 'ROFR'
                            ? 'concept-rofr'
                            : group.status === 'Negotiation'
                              ? 'concept-negotiation'
                              : 'concept-player-decision'
                        "
                      >
                        {{ group.status }}
                      </concept-link>
                    </div>
                  </div>
                </div>
              </div>

              <!-- New Team -->
              <div class="transfer-card__team-wrapper">
                <div class="transfer-card__team">
                  <NuxtImg
                    v-if="getTeamLogo(group.mainTransfer.newTeamId)"
                    :src="setAliasUrl(getTeamLogo(group.mainTransfer.newTeamId)!)"
                    :alt="getTeamName(group.mainTransfer.newTeamId)"
                    width="48"
                    height="48"
                    sizes="48px"
                    class="transfer-card__logo"
                    loading="lazy"
                  />
                  <div v-else class="transfer-card__logo-placeholder"></div>
                  <p class="transfer-card__team-name hidden sm:block">
                    {{ getTeamName(group.mainTransfer.newTeamId) }}
                  </p>
                  <p class="transfer-card__team-name sm:hidden">
                    {{ getTeamShortName(group.mainTransfer.newTeamId) }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Related Transfers -->
            <div v-if="group.relatedTransfers.length > 0" class="transfer-card__related-flows">
              <div class="h-px bg-gray-700 w-full max-w-[80%]"></div>
              <div
                v-for="(relatedTransfer, relatedIndex) in group.relatedTransfers"
                :key="`${relatedTransfer.playerId}-${relatedTransfer.date}-${relatedIndex}`"
                class="transfer-card__related-flow"
              >
                <!-- Old Team -->
                <div class="transfer-card__team-wrapper">
                  <div class="transfer-card__team">
                    <NuxtImg
                      v-if="getTeamLogo(relatedTransfer.oldTeamId)"
                      :src="setAliasUrl(getTeamLogo(relatedTransfer.oldTeamId)!)"
                      :alt="getTeamName(relatedTransfer.oldTeamId)"
                      width="48"
                      height="48"
                      sizes="48px"
                      class="transfer-card__logo"
                      loading="lazy"
                    />
                    <div v-else class="transfer-card__logo-placeholder"></div>
                    <p class="transfer-card__team-name hidden sm:block">
                      {{ getTeamName(relatedTransfer.oldTeamId) }}
                    </p>
                    <p class="transfer-card__team-name sm:hidden">
                      {{ getTeamShortName(group.mainTransfer.oldTeamId) }}
                    </p>
                  </div>
                </div>

                <!-- Player with Arrow -->
                <div class="transfer-card__player-section">
                  <div class="transfer-card__arrow-line">
                    <div
                      :class="[
                        'transfer-card__arrow-left',
                        { dashed: group.status && isTransferActive(group.status) },
                      ]"
                    ></div>
                    <div class="transfer-card__player-badge transfer-card__player-badge--related">
                      <p class="transfer-card__player-name">{{ relatedTransfer.playerName }}</p>
                    </div>
                    <div
                      :class="[
                        'transfer-card__arrow-right',
                        { dashed: group.status && isTransferActive(group.status) },
                      ]"
                    ></div>
                  </div>
                </div>

                <!-- New Team -->
                <div class="transfer-card__team-wrapper">
                  <div class="transfer-card__team">
                    <NuxtImg
                      v-if="getTeamLogo(relatedTransfer.newTeamId)"
                      :src="setAliasUrl(getTeamLogo(relatedTransfer.newTeamId)!)"
                      :alt="getTeamName(relatedTransfer.newTeamId)"
                      width="48"
                      height="48"
                      sizes="48px"
                      class="transfer-card__logo"
                      loading="lazy"
                    />
                    <div v-else class="transfer-card__logo-placeholder"></div>
                    <p class="transfer-card__team-name hidden sm:block">
                      {{ getTeamName(relatedTransfer.newTeamId) }}
                    </p>
                    <p class="transfer-card__team-name sm:hidden">
                      {{ getTeamShortName(group.mainTransfer.newTeamId) }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Right Info -->
          <div class="transfer-card__right-info">
            <concept-link
              v-if="group.transferType && getTransferTypeConceptId(group.transferType)"
              :concept="getTransferTypeConceptId(group.transferType)!"
              class="transfer-card__type"
            >
              {{ $t(`mercato.${group.transferType}`) }}
            </concept-link>
            <span v-else-if="group.transferType" class="transfer-card__type">{{ group.transferType }}</span>
            <span
              v-if="group.mainTransfer.paid && group.mainTransfer.paid !== ''"
              class="transfer-card__paid"
            >
              {{ group.mainTransfer.paid === "0" ? "0 M" : `${$tn(Number(group.mainTransfer.paid))} M` }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="empty-state">
      <p>{{ $t("mercato.noTransfersAvailable") }}</p>
    </div>
  </section>
</template>

<style scoped>
/* Status Filter Buttons */
.status-filter-btn {
  @apply px-4 py-2 text-sm w-1/2 md:w-52 md:text-base uppercase font-semibold bg-gray-800 border border-gray-600 hover:bg-gray-600 transition-colors duration-150;
}

.status-filter-btn.active {
  @apply bg-mercato text-black border-mercato;
}

/* Transfers List */
.transfers-list {
  @apply flex flex-col gap-2.5 mx-auto;
}

/* Transfer Card - 3 Column Layout */
.transfer-card {
  @apply flex-col gap-2.5 p-2.5 bg-gray-800 rounded-lg md:p-3 flex md:flex-row items-center md:gap-3;
}

/* Left Info Column */
.transfer-card__left-info {
  @apply flex-row w-full justify-between flex gap-1.5 items-center md:justify-start md:w-max flex-shrink-0;
}

.transfer-card__window {
  @apply text-gray-400 text-[10px] uppercase;
}

/* Scope with Icons */
.transfer-card__scope {
  @apply flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold uppercase;
}

.transfer-card__scope--global {
  @apply bg-blue-600 bg-opacity-20 text-blue-400 border border-blue-600;
}

.transfer-card__scope--local {
  @apply bg-purple-600 bg-opacity-20 text-purple-400 border border-purple-600;
}

.transfer-card__scope-icon {
  @apply w-3 h-3 flex-shrink-0;
}

/* Transfer Card Group Wrapper */
.transfer-card-group {
  @apply w-full;
}

/* Main Flow */
.transfer-card__main-flow {
  @apply w-full gap-1.5 flex items-center justify-center md:gap-3;
}

/* Related Transfers Container */
.transfer-card__related-flows {
  @apply flex flex-col gap-2 mt-2 pt-2 items-center justify-center;
}

/* Individual Related Transfer Flow */
.transfer-card__related-flow {
  @apply w-full gap-1.5 flex items-center justify-center md:gap-3 opacity-80;
}

/* Related Player Badge */
.transfer-card__player-badge--related {
  @apply bg-gray-800 border-gray-600;
}

/* Center: Transfer Flow */
.transfer-card__flow {
  @apply flex flex-col gap-0 w-full md:gap-0 flex-1;
}

.transfer-card__team-wrapper {
  @apply flex flex-col items-center gap-0.5 md:gap-1 relative w-[10%] shrink-0;
}

.transfer-card__team {
  @apply flex flex-col items-center gap-1;
}

.transfer-card__logo {
  @apply min-w-8 w-10 h-10 rounded-lg shrink-0 object-contain;
}

.transfer-card__logo-placeholder {
  @apply w-10 h-10 bg-gray-600 rounded-full shrink-0;
}

.transfer-card__team-name {
  @apply text-[9px] font-medium md:text-[10px] text-center leading-none;
}

/* Player Section with Arrows */
.transfer-card__player-section {
  @apply flex flex-col items-center gap-1 flex-1 min-w-0 max-w-full md:max-w-[60%] relative;
}

.transfer-card__arrow-line {
  @apply flex items-center gap-1.5 md:gap-2 w-full;
}

.transfer-card__arrow-left,
.transfer-card__arrow-right {
  @apply h-px bg-mercato flex-1 relative;
}

/* Dashed arrows for active transfers */
.transfer-card__arrow-left.dashed,
.transfer-card__arrow-right.dashed {
  background-image: linear-gradient(to right, theme("colors.mercato") 50%, transparent 50%);
  background-size: 8px 1px;
  background-repeat: repeat-x;
  background-color: transparent;
}

/* Arrow triangle (only for non-dashed) */
.transfer-card__arrow-right:where([dir="ltr"], [dir="ltr"] *)::after {
  content: "";
  @apply absolute right-0 top-1/2 -translate-y-1/2 w-0 h-0;
  border-left: 6px solid theme("colors.mercato");
  border-top: 4px solid transparent;
  border-bottom: 4px solid transparent;
}

.transfer-card__arrow-right:where([dir="rtl"], [dir="rtl"] *)::after {
  content: "";
  @apply absolute left-0 top-1/2 -translate-y-1/2 w-0 h-0;
  border-right: 6px solid theme("colors.mercato");
  border-top: 4px solid transparent;
  border-bottom: 4px solid transparent;
}

/* Status badge on arrow (desktop) */
.transfer-card__status-on-arrow {
  @apply absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-full bg-orange-700 bg-opacity-90 text-orange-100 px-2 py-0.5 rounded text-[9px] uppercase font-bold whitespace-nowrap -mt-2;
}

/* Status badge on left-info (mobile) */
.transfer-card__status-mobile {
  @apply bg-orange-700 bg-opacity-90 text-orange-100 px-2 py-0.5 rounded text-[9px] uppercase font-bold whitespace-nowrap;
}

.transfer-card__player-badge {
  @apply bg-gray-900 px-2.5 py-1.5 rounded border border-mercato flex-shrink-0;
}

.transfer-card__player-name {
  @apply text-[11px] max-w-[100px] text-xs md:text-sm font-semibold text-white text-center leading-none sm:max-w-[120px] md:max-w-[180px];
}

/* Right Info Column */
.transfer-card__right-info {
  @apply w-full justify-center items-center flex flex-col gap-1 md:items-end md:w-28 flex-shrink-0;
}

.transfer-card__type {
  @apply bg-gray-700 text-gray-200 px-2 py-0.5 rounded text-[10px] font-semibold uppercase;
}

.transfer-card__paid {
  @apply text-mercato font-bold text-sm;
}

/* Empty State */
.empty-state {
  @apply text-center py-12 text-gray-400 text-lg;
}
</style>
